import { fetchComCatBrandCatalog, type ComCatBrandItem } from '@/api/comCatBrand';
import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { surveyPathAfterSmokerProfile } from '@/config/codeBrand';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import {
  selectCodeBrand,
  selectPdpaConsent,
  selectSurveyScreening,
  selectUniqueCodeDraft,
} from '@/stores/selectors';
import {
  NATIONALITY_MALAYSIAN,
  NATIONALITY_NON_MALAYSIAN,
  readSmokerProfileAnswers,
  writeSmokerProfileAnswers,
  type SmokerNationality,
} from '@/lib/smokerProfileStorage';
import { WORLD_COUNTRIES } from '@/lib/worldCountries';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const RACES = ['Malay', 'Chinese', 'Indian', 'Others'] as const;

const NATIONALITIES = [NATIONALITY_MALAYSIAN, NATIONALITY_NON_MALAYSIAN] as const;

function raceOptionLabel(
  race: (typeof RACES)[number],
  labels: {
    raceMalay: string;
    raceChinese: string;
    raceIndian: string;
    raceOthers: string;
  }
): string {
  switch (race) {
    case 'Malay':
      return labels.raceMalay;
    case 'Chinese':
      return labels.raceChinese;
    case 'Indian':
      return labels.raceIndian;
    case 'Others':
      return labels.raceOthers;
    default:
      return race;
  }
}

function nationalityOptionLabel(
  nationality: SmokerNationality,
  labels: {
    nationalityMalaysian: string;
    nationalityNonMalaysian: string;
  }
): string {
  return nationality === NATIONALITY_MALAYSIAN
    ? labels.nationalityMalaysian
    : labels.nationalityNonMalaysian;
}

const MY_STATES = [
  'Johor',
  'Kedah',
  'Kelantan',
  'Kuala Lumpur',
  'Melaka',
  'Negeri Sembilan',
  'Pahang',
  'Perak',
  'Perlis',
  'Pulau Pinang',
  'Sabah',
  'Sarawak',
  'Selangor',
  'Terengganu',
] as const;

type StateLabels = {
  stateJohor: string;
  stateKedah: string;
  stateKelantan: string;
  stateKualaLumpur: string;
  stateMelaka: string;
  stateNegeriSembilan: string;
  statePahang: string;
  statePerak: string;
  statePerlis: string;
  statePulauPinang: string;
  stateSabah: string;
  stateSarawak: string;
  stateSelangor: string;
  stateTerengganu: string;
};

function stateOptionLabel(state: (typeof MY_STATES)[number], labels: StateLabels): string {
  switch (state) {
    case 'Johor':
      return labels.stateJohor;
    case 'Kedah':
      return labels.stateKedah;
    case 'Kelantan':
      return labels.stateKelantan;
    case 'Kuala Lumpur':
      return labels.stateKualaLumpur;
    case 'Melaka':
      return labels.stateMelaka;
    case 'Negeri Sembilan':
      return labels.stateNegeriSembilan;
    case 'Pahang':
      return labels.statePahang;
    case 'Perak':
      return labels.statePerak;
    case 'Perlis':
      return labels.statePerlis;
    case 'Pulau Pinang':
      return labels.statePulauPinang;
    case 'Sabah':
      return labels.stateSabah;
    case 'Sarawak':
      return labels.stateSarawak;
    case 'Selangor':
      return labels.stateSelangor;
    case 'Terengganu':
      return labels.stateTerengganu;
    default:
      return state;
  }
}

const selectClassName =
  'min-w-0 w-full rounded-sm border border-slate-300/90 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 outline-none ring-0 focus:border-slate-400 focus:ring-1 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-70';

const titleClass =
  'm-0 mb-2 text-left text-[16px] font-medium text-white/95 sm:text-[17px]';

/** Vertical rhythm between profile fields (tighter so 5 fields fit short viewports). */
const fieldGapClass = 'mt-3.5';

function readInitialDraft(): {
  nationality: SmokerNationality | '';
  country: string;
  race: string;
  state: string;
  brand: string;
  variant: string;
} {
  const saved = readSmokerProfileAnswers();
  const raw = saved?.nationality?.trim() ?? '';
  const country = typeof saved?.country === 'string' ? saved.country : '';
  const race = typeof saved?.race === 'string' ? saved.race : '';
  const state = typeof saved?.state === 'string' ? saved.state : '';
  const brand = typeof saved?.brand === 'string' ? saved.brand : '';
  const variant = typeof saved?.variant === 'string' ? saved.variant : '';

  if (raw === NATIONALITY_MALAYSIAN) {
    return { nationality: NATIONALITY_MALAYSIAN, country: '', race, state, brand, variant };
  }

  // Incomplete Non-Malaysian (no country yet) → show default Race/State UI, not Country.
  if (raw === NATIONALITY_NON_MALAYSIAN && country.trim()) {
    return {
      nationality: NATIONALITY_NON_MALAYSIAN,
      country,
      race: '',
      state: '',
      brand,
      variant,
    };
  }

  return { nationality: '', country: '', race: '', state: '', brand, variant };
}

export default function SmokerProfile() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const codeBrand = useAppSelector(selectCodeBrand);
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const [initialDraft] = useState(readInitialDraft);
  const [nationality, setNationality] = useState<SmokerNationality | ''>(
    () => initialDraft.nationality
  );
  const [country, setCountry] = useState(() => initialDraft.country);
  const [race, setRace] = useState(() => initialDraft.race);
  const [state, setState] = useState(() => initialDraft.state);
  const [brand, setBrand] = useState(() => initialDraft.brand);
  const [variant, setVariant] = useState(() => initialDraft.variant);
  const [nextWarning, setNextWarning] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<ComCatBrandItem[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(true);

  const isNonMalaysian = nationality === NATIONALITY_NON_MALAYSIAN;
  const isMalaysian = nationality === NATIONALITY_MALAYSIAN;
  /** Race/State stay on screen until Non-Malaysian; disabled until a nationality is chosen. */
  const showRaceAndState = !isNonMalaysian;
  const raceStateDisabled = !isMalaysian;

  const brands = catalog.map((item) => item.brand);
  const variants = brand
    ? (catalog.find((item) => item.brand === brand)?.variants ?? [])
    : [];

  useEffect(() => {
    if (surveyLanguage == null || surveyScreening == null || !pdpaConsent) {
      return;
    }
    if (!mobileLocalDigits.replace(/\D/g, '') || !customerUuid) {
      return;
    }

    let cancelled = false;
    setBrandsLoading(true);
    void (async () => {
      try {
        const list = await fetchComCatBrandCatalog();
        if (!cancelled) {
          setCatalog(list);
        }
      } catch {
        if (!cancelled) {
          setNextWarning(t.smokerProfile.brandsLoadError);
        }
      } finally {
        if (!cancelled) {
          setBrandsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [surveyLanguage, surveyScreening, pdpaConsent, mobileLocalDigits, customerUuid, t.smokerProfile.brandsLoadError]);

  useEffect(() => {
    writeSmokerProfileAnswers({
      name: '',
      email: '',
      nationality,
      country: isNonMalaysian ? country : '',
      race: isNonMalaysian ? '' : race,
      state: isNonMalaysian ? '' : state,
      brand,
      variant,
    });
  }, [nationality, country, race, state, brand, variant, isNonMalaysian]);

  useEffect(() => {
    setNextWarning(null);
  }, [nationality, country, race, state, brand, variant]);

  const handleNationalityChange = (value: string) => {
    if (value !== NATIONALITY_MALAYSIAN && value !== NATIONALITY_NON_MALAYSIAN && value !== '') {
      return;
    }
    setNationality(value);
    if (value === NATIONALITY_NON_MALAYSIAN) {
      setRace('');
      setState('');
    } else {
      setCountry('');
      if (value === '') {
        setRace('');
        setState('');
      }
    }
  };

  const handleNext = () => {
    if (brandsLoading) {
      setNextWarning(t.smokerProfile.brandsLoadingWarning);
      return;
    }

    if (!nationality) {
      setNextWarning(t.smokerProfile.incompleteWarning);
      return;
    }

    const b = brand.trim();
    const v = variant.trim();
    if (!b || !v) {
      setNextWarning(t.smokerProfile.incompleteWarning);
      return;
    }

    if (nationality === NATIONALITY_NON_MALAYSIAN) {
      const c = country.trim();
      if (!c) {
        setNextWarning(t.smokerProfile.incompleteWarning);
        return;
      }
      writeSmokerProfileAnswers({
        name: '',
        email: '',
        nationality,
        country: c,
        race: '',
        state: '',
        brand: b,
        variant: v,
      });
    } else {
      const r = race.trim();
      const s = state.trim();
      if (!r || !s) {
        setNextWarning(t.smokerProfile.incompleteWarning);
        return;
      }
      writeSmokerProfileAnswers({
        name: '',
        email: '',
        nationality,
        country: '',
        race: r,
        state: s,
        brand: b,
        variant: v,
      });
    }

    setNextWarning(null);
    navigate(surveyPathAfterSmokerProfile(codeBrand));
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }

  return (
    <SurveyBackgroundLayout>
      {/*
        Avoid justify-center + overflow-hidden (crops Nationality on short viewports).
        Scroll when needed; keep top/bottom padding so the first field stays fully visible.
      */}
      <div className="relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
        <div className="mx-auto flex w-full max-w-full flex-col py-5 pt-8 pb-8 sm:pt-10">
          <SurveyBrownPanelInset>
            <p className={titleClass}>{t.smokerProfile.nationalityLabel}</p>
            <SurveyBrownPanel className="!p-3.5">
              <select
                id="smoker-nationality"
                name="nationality"
                value={nationality}
                onChange={(e) => handleNationalityChange(e.target.value)}
                className={selectClassName}
              >
                <option value="">{t.smokerProfile.selectNationality}</option>
                {NATIONALITIES.map((n) => (
                  <option key={n} value={n}>
                    {nationalityOptionLabel(n, t.smokerProfile)}
                  </option>
                ))}
              </select>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          {showRaceAndState ? (
            <>
              <SurveyBrownPanelInset className={fieldGapClass}>
                <p className={titleClass}>{t.smokerProfile.raceLabel}</p>
                <SurveyBrownPanel className="!p-3.5">
                  <select
                    id="smoker-race"
                    name="race"
                    value={race}
                    onChange={(e) => setRace(e.target.value)}
                    disabled={raceStateDisabled}
                    className={selectClassName}
                  >
                    <option value="">{t.smokerProfile.selectRace}</option>
                    {RACES.map((r) => (
                      <option key={r} value={r}>
                        {raceOptionLabel(r, t.smokerProfile)}
                      </option>
                    ))}
                  </select>
                </SurveyBrownPanel>
              </SurveyBrownPanelInset>

              <SurveyBrownPanelInset className={fieldGapClass}>
                <p className={titleClass}>{t.smokerProfile.stateLabel}</p>
                <SurveyBrownPanel className="!p-3.5">
                  <select
                    id="smoker-state"
                    name="state"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    disabled={raceStateDisabled}
                    className={selectClassName}
                  >
                    <option value="">{t.smokerProfile.selectState}</option>
                    {MY_STATES.map((s) => (
                      <option key={s} value={s}>
                        {stateOptionLabel(s, t.smokerProfile)}
                      </option>
                    ))}
                  </select>
                </SurveyBrownPanel>
              </SurveyBrownPanelInset>
            </>
          ) : (
            <SurveyBrownPanelInset className={fieldGapClass}>
              <p className={titleClass}>{t.smokerProfile.countryLabel}</p>
              <SurveyBrownPanel className="!p-3.5">
                <select
                  id="smoker-country"
                  name="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className={selectClassName}
                >
                  <option value="">{t.smokerProfile.selectCountry}</option>
                  {WORLD_COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </SurveyBrownPanel>
            </SurveyBrownPanelInset>
          )}

          <SurveyBrownPanelInset className={fieldGapClass}>
            <p className={titleClass}>{t.smokerProfile.brandLabel}</p>
            <SurveyBrownPanel className="!p-3.5">
              <div className="flex flex-col gap-2.5">
                <select
                  id="smoker-brand"
                  name="brand"
                  value={brand}
                  onChange={(e) => {
                    setBrand(e.target.value);
                    setVariant('');
                  }}
                  className={selectClassName}
                  aria-label="Select a brand"
                  disabled={brandsLoading}
                >
                  <option value="">
                    {brandsLoading ? t.smokerProfile.loadingBrands : t.smokerProfile.selectBrand}
                  </option>
                  {brands.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
                <select
                  id="smoker-variant"
                  name="variant"
                  value={variant}
                  onChange={(e) => setVariant(e.target.value)}
                  className={selectClassName}
                  aria-label="Select a variant"
                  disabled={!brand || brandsLoading}
                >
                  <option value="">
                    {!brand
                      ? t.smokerProfile.selectBrandFirst
                      : t.smokerProfile.selectVariant}
                  </option>
                  {variants.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-6">
            <div className="flex flex-col items-center gap-3">
              {nextWarning != null ? <SurveyValidationAlert>{nextWarning}</SurveyValidationAlert> : null}
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                onClick={handleNext}
              >
                {t.smokerProfile.continue}
              </MetallicPillButton>
            </div>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
